import React, { useState, useLayoutEffect, useRef, useEffect } from 'react'
import propTypes from 'prop-types'
import Group from '../../common/Group/Group.js'
import SubNav from '../../common/SubNav/SubNav'
import { useAuth } from '../../../context/Auth'
import { useBp, useBpListener } from '../../../utils/Breakpoints/Breakpoints'
import { gsap } from 'gsap'
import { useGSAP } from '@gsap/react'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import { useLocation } from 'react-router-dom'

import './ContentTemplate.scss'

const ContentTemplate = ({ children }) => {
  const aboveSm = useBp('> sm')
  const belowXs = useBp('< xs')
  const belowMd = useBp('< md')
  const layout = aboveSm ? 'col-2' : belowMd ? 'col-1' : ''

  const { currentUser } = useAuth()
  const location = useLocation()
  const wrapperRef = useRef()

  const [navOpen, setNavOpen] = useState(false)
  const [pinNav, setPinNav] = useState(() => {
    const saved = localStorage.getItem('portfolioPinNav')
    return saved !== null ? saved === 'true' : true
  })

  useEffect(() => {
    localStorage.setItem('portfolioPinNav', pinNav)
  }, [pinNav])
  const [links, setLinks] = useState([])

  useLayoutEffect(() => {
    setNavOpen(false)
  }, [aboveSm])

  useEffect(() => {
    // Create a MutationObserver to watch for DOM changes
    const observer = new MutationObserver(() => {
      const updatedLinks = gsap.utils.toArray(
        '.sub-nav .link-group button.link'
      )
      setLinks(updatedLinks)
    })

    // Observe changes to the wrapperRef's child nodes
    if (wrapperRef.current) {
      observer.observe(wrapperRef.current, { childList: true, subtree: true })
    }

    // Cleanup the observer when the component unmounts or the route changes
    return () => {
      observer.disconnect()
    }
  }, [location.pathname])

  gsap.registerPlugin(ScrollTrigger, ScrollToPlugin, useGSAP)

  useGSAP(
    () => {
      links.forEach((link) => {
        let element = document.querySelector(`#${link.classList[1]}`)

        const condValue = belowMd && pinNav ? 50 : 0

        let linkTrigger = ScrollTrigger.create({
          trigger: element,
          start: `top ${72 + condValue}px`,
        })

        ScrollTrigger.create({
          trigger: element,
          start: 'top center',
          end: 'bottom center',
          onToggle: (self) => {
            self.isActive && setActive(link)
          },
        })

        const clickHandler = () => {
          gsap.to(window, {
            duration: 0.5,
            scrollTo: linkTrigger.start,
            overwrite: 'auto',
          })
          pinNav && belowMd && setTimeout(() => setNavOpen(false), 800)
        }

        link.addEventListener('click', clickHandler)

        // Cleanup function for this link
        return () => {
          link.removeEventListener('click', clickHandler)
        }
      })

      let goToTop = document.querySelector('.sub-nav button.link.go-to-top')

      if (goToTop) {
        const goToTopHandler = (e) => {
          e.preventDefault()
          gsap.to(window, {
            duration: 0.5,
            scrollTo: 0,
            overwrite: 'auto',
          })
          pinNav && belowMd && setTimeout(() => setNavOpen(false), 800)
        }

        goToTop.addEventListener('click', goToTopHandler)

        // Cleanup function for the go-to-top button
        return () => {
          goToTop.removeEventListener('click', goToTopHandler)
        }
      }

      function setActive(link) {
        links.forEach((el) => el.classList.remove('active'))
        link.classList.add('active')
      }

      // Cleanup ScrollTriggers
      return () => {
        ScrollTrigger.getAll().forEach((trigger) => trigger.kill())
      }
    },
    {
      dependencies: [
        navOpen,
        pinNav,
        useBpListener(),
        links,
        location.pathname,
      ],
      revertOnUpdate: true,
    }
  )

  // Get the title prop from each Section component and create new array
  // If `children` is not an array (not likely), put the single object in an array
  const sections = Array.isArray(children)
    ? // Check if user is logged in
      currentUser
      ? // If so, map through children
        children.map((child) =>
          // Check if the child has a child, get the title either way
          child.props.title
            ? child.props.title
            : child.props.children.props.title
        )
      : // If not, filter out any non-Section components, then map through the titles
        children
          .filter((child) => child.props.title)
          .map((child) => child.props.title)
    : // If there's only one child, put it in an array
      [children.props.title]

  const subNavProps = {
    links: sections,
    toggleNavOpen: () => setNavOpen(!navOpen),
    togglePinNav: () => setPinNav(!pinNav),
    setNavOpen,
    navOpen,
    pinNav,
    sizing: { belowMd, belowXs },
  }

  return (
    <div className='content-wrapper' ref={wrapperRef}>
      <Group row className='section-container'>
        <Group column className={`sections ${layout}`}>
          {belowMd && <SubNav inline {...subNavProps} />}
          {belowMd && <div className='sub-nav-placeholder' />}
          {children}
        </Group>
        {/* Populate array of links from child Sections */}
        {aboveSm && <SubNav {...subNavProps} />}
      </Group>
    </div>
  )
}

ContentTemplate.propTypes = {
  children: propTypes.oneOfType([
    propTypes.array,
    propTypes.shape(),
    propTypes.string,
  ]).isRequired,
}

export default ContentTemplate
